import React, { memo, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { reduxForm } from "redux-form";
import { forms } from "utils/constants";
import { gotoUrl, urlParam } from "utils/helpers";
import Spinner, { styles } from "components/Spinner";
import NotEligible from "components/NotEligible";
import { paths } from "utils/constants";
import get from "lodash/get";
import { createStructuredSelector } from "reselect";
import {
  uploadImage,
  fetchAddressLookup,
  fetchDeliverySlots,
  resetDeliverySlots,
  fetchPostOfficeDeliverySlots,
  validatePostalCode,
  verifyIdentity,
  resetPostalCodeValidation,
  fetchUploadIdentityDetails,
  submitUploadIdentity,
  clearData,
  changeFormField
} from "ducks/actions";
import {
  makeSelectCountries,
  makeSelectStates,
  makeSelectOtherCountries,
  makeSelectPrimaryDocTypes,
  makeSelectMinimumAge,
  makeSelectDateOptions,
  makeSelectMonthOptions,
  makeSelectYearOptions,
  makeSelectIsUploadIDLater,
  makeSelectPageValidations,
  makeSelectIsFetchingData,
  makeSelectIsImageUploaded,
  makeSelectUploadIdentityFormInitialValues,
  makeSelectUploadIdentityForm,
  makeSelectSelectedNumber,
  makeSelectDeliveryMethod,
  makeSelectDeliveryMethods,
  makeSelectDeliveryDates,
  makeSelectFreeDeliverySlots,
  makeSelectSpecialDeliverySlots,
  makeSelectPostOfficeRegions,
  makeSelectPostOfficeDeliverySlots,
  makeSelectSelectedPostOffice,
  makeSelectPostalCodeValidation,
  makeSelectUploadIdentityDetails,
  makeSelectUploadIdentityPayload,
  makeSelectCanUploadIdentity,
  makeSelectIsIdentityVerified,
  makeSelectIsIdentityVerificationFailed,
  makeSelectCombinedDeliveryDates,
  makeSelectSessionToken,
  makeSelectDocTypes,
  makeSelectDropIdentityValidations,
  makeSelectNricIssueYearOptions,
  makeSelecMessageData,
  makeSelectImageUploading,
  makeSelectImageUploaded
} from "ducks/selectors";
import SmartRender from "containers/SmartRender";
import validate, { asyncValidate } from "validations";

const UploadIdentityHOC = Component =>
  function Comp(props) {
    const {
      match,
      fetchUploadIdentityDetails,
      isFetchingUploadIdentityDetails,
      uploadIdentityDetails,
      canUploadIdentity,
      token
    } = props;

    const { type } = get(match, "params", {});
    const orderItemType = uploadIdentityDetails?.order_item_type;

    useEffect(
      () => {
        if (token) {
          fetchUploadIdentityDetails({
            type,
            token
          });
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [token, type]
    );

    if (isFetchingUploadIdentityDetails)
      return <Spinner size={200} sx={styles.fullPage} />;

    if (canUploadIdentity && token) {
      return <Component {...props} orderItemType={orderItemType} />;
    }

    return <NotEligible description={uploadIdentityDetails.reason} />;
  };

const mapStateToProps = (_, props) =>
  createStructuredSelector({
    formValidations: makeSelectPageValidations(forms.UPLOAD_IDENTITY_FORM),
    formValues: makeSelectUploadIdentityForm(),
    payload: makeSelectUploadIdentityPayload(),
    uploadIdentityDetails: makeSelectUploadIdentityDetails(),
    isFetchingUploadIdentityDetails: makeSelectIsFetchingData(
      "uploadIdentityDetails"
    ),
    token: makeSelectSessionToken(),
    issuingStates: makeSelectStates(),
    issuingCountries: makeSelectCountries(),
    topCountries: makeSelectCountries(),
    otherCountries: makeSelectOtherCountries(),
    primaryDocTypes: makeSelectPrimaryDocTypes(),
    minimumAge: makeSelectMinimumAge(),
    monthOptions: makeSelectMonthOptions(),
    docTypes: makeSelectDocTypes(),
    dateOptions: makeSelectDateOptions(),
    yearOptions: makeSelectYearOptions(),
    doiYearOptions: makeSelectNricIssueYearOptions(),
    isIdentityVerified: makeSelectIsIdentityVerified(),
    uploadIDLater: makeSelectIsUploadIDLater(),
    frontImageUploading: makeSelectIsFetchingData("primary_front_ImagesUpload"),
    backImageUploading: makeSelectIsFetchingData("primary_back_ImagesUpload"),
    frontImageUploaded: makeSelectIsImageUploaded("primary_front_ImagesUpload"),
    backImageUploaded: makeSelectIsImageUploaded("primary_back_ImagesUpload"),
    imageUploading: makeSelectImageUploading(),
    imageUploaded: makeSelectImageUploaded(),
    selectedNumber: makeSelectSelectedNumber(),
    deliveryMethods: makeSelectDeliveryMethods(),
    deliverySlotDates: makeSelectDeliveryDates(),
    freeDeliverySlotTime: makeSelectFreeDeliverySlots(),
    specialDeliverySlotTime: makeSelectSpecialDeliverySlots(),
    postOfficeRegions: makeSelectPostOfficeRegions(),
    postOfficeDeliverySlots: makeSelectPostOfficeDeliverySlots(),
    combinedDeliverySlotDates: makeSelectCombinedDeliveryDates(),
    selectedPostOffice: makeSelectSelectedPostOffice(),
    postalCodeValidation: makeSelectPostalCodeValidation(),
    canUploadIdentity: makeSelectCanUploadIdentity(),
    isVerifyingIdentity: makeSelectIsFetchingData("verifyIdentity"),
    isIdentityVerificationFailed: makeSelectIsIdentityVerificationFailed(),
    dropIdentityValidations: makeSelectDropIdentityValidations(),

    messageData: makeSelecMessageData(),
    initialValues: state => {
      return {
        ...makeSelectUploadIdentityFormInitialValues(props)(state),
        delivery_method: makeSelectDeliveryMethod()(state)
      };
    }
  });

export const mapDispatchToProps = dispatch => {
  return {
    fetchUploadIdentityDetails: params => {
      dispatch(fetchUploadIdentityDetails(params));
    },
    uploadImage: (...payload) => dispatch(uploadImage(payload)),
    fetchDeliverySlots: params => {
      dispatch(fetchDeliverySlots(params));
    },
    resetDeliverySlots: () => dispatch(resetDeliverySlots()),
    fetchPostOfficeDeliverySlots: params => {
      dispatch(fetchPostOfficeDeliverySlots(params));
    },
    resetVerifyIdentity: () => dispatch(clearData("verifyIdentity")),
    validatePostalCode: (postal_code, field) => {
      dispatch(validatePostalCode({ postal_code }, field));
    },
    resetPostalCodeValidation: field => {
      dispatch(resetPostalCodeValidation(field));
    },
    fetchAddressLookup: params => {
      dispatch(fetchAddressLookup(params));
    },
    onSubmit: (navigate, payload) => {
      const {
        identity_verified,
        verificationPayload,
        type,
        skip_id_verification
      } = payload;
      const returnUrl = urlParam("return_url") || paths.PAYMENT_RETURN_V3;

      if (identity_verified || skip_id_verification) {
        dispatch(
          submitUploadIdentity(payload, (result, error) => {
            if (result["status"] === "ok") {
              gotoUrl(returnUrl, true);
            }
          })
        );
      } else {
        dispatch(verifyIdentity({ ...verificationPayload, type }));
      }
    },
    changeField: (field, value) => {
      dispatch(
        changeFormField({
          form: forms.UPLOAD_IDENTITY_FORM,
          field: field,
          value: value
        })
      );
    }
  };
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(
  withConnect,
  reduxForm({
    form: forms.UPLOAD_IDENTITY_FORM,
    destroyOnUnmount: false,
    validate,
    asyncValidate,
    enableReinitialize: true
  }),
  memo
)(UploadIdentityHOC(SmartRender));
